import React, { useEffect, useState, useCallback } from 'react';
import { objectionsAPI } from '../../../services/api';
import { Button, Card, Modal } from '../../../components/ui';
import toast from 'react-hot-toast';

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };

const STATUS_COLORS = {
  Open:       { bg: '#fef9c3', color: '#a16207', border: '#fde047' },
  Addressed:  { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  Resolved:   { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
  Unresolved: { bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
};

const CATEGORY_ICONS = {
  Fee: '💰', Timing: '⏰', Authority: '👔', Competition: '🏆',
  Relevance: '🎯', Trust: '🤝', Logistics: '🏗️', Other: '💬'
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  };
  return (
    <button onClick={copy} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: copied ? '#dcfce7' : '#f8fafc', color: copied ? '#15803d' : '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

export default function BattleCardTab({ leadId }) {
  const [library, setLibrary]     = useState([]);
  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(null); // logId being generated

  // Log-objection modal state
  const [modal, setModal]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm]     = useState({
    objection_id:       '',
    objection_text:     '',
    objection_category: '',
    raised_by_contact:  '',
  });

  // Expanded card (to show AI responses inline)
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const [libRes, logRes] = await Promise.all([
        objectionsAPI.getAll(),
        objectionsAPI.getByLead(leadId),
      ]);
      setLibrary(libRes.data?.data || []);
      setLogs(logRes.data?.data || []);
    } catch {
      toast.error('Failed to load battle card data');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  // When a library objection is selected, pre-fill text + category
  const onSelectObjection = (e) => {
    const id = e.target.value;
    const found = library.find(o => o._id === id);
    setForm(f => ({
      ...f,
      objection_id:       id,
      objection_text:     found ? found.objection_text : '',
      objection_category: found ? found.category : '',
    }));
  };

  const openModal = () => {
    setForm({ objection_id: '', objection_text: '', objection_category: '', raised_by_contact: '' });
    setModal(true);
  };

  const logObjection = async () => {
    if (!form.objection_text.trim()) { toast.error('Enter the objection text'); return; }
    setSaving(true);
    try {
      const res = await objectionsAPI.log(leadId, {
        objection_id:       form.objection_id || null,
        objection_text:     form.objection_text.trim(),
        objection_category: form.objection_category || null,
        raised_by_contact:  form.raised_by_contact.trim() || null,
      });
      setLogs(prev => [res.data?.data, ...prev]);
      setModal(false);
      toast.success('Objection logged');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to log');
    } finally { setSaving(false); }
  };

  const generateResponse = async (log) => {
    setGenerating(log._id);
    try {
      const res = await objectionsAPI.generate(leadId, log._id);
      const updated = res.data?.data;
      setLogs(prev => prev.map(l => l._id === updated._id ? updated : l));
      setExpanded(updated._id);
      toast.success('AI responses generated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'AI generation failed');
    } finally { setGenerating(null); }
  };

  const updateStatus = async (log, status) => {
    try {
      const res = await objectionsAPI.updateLog(leadId, log._id, { status });
      setLogs(prev => prev.map(l => l._id === log._id ? { ...l, status: res.data?.data?.status || status } : l));
    } catch {
      toast.error('Failed to update status');
    }
  };

  if (loading) return <p style={{ color: '#94a3b8', fontSize: 13, padding: 24, textAlign: 'center' }}>Loading…</p>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', margin: 0 }}>⚔️ Objection Battle Card</h3>
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Log objections raised by the lead — get AI-generated verbal, WhatsApp &amp; email responses instantly.</p>
        </div>
        <Button variant="primary" size="sm" onClick={openModal}>+ Log Objection</Button>
      </div>

      {/* Empty state */}
      {logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
          <p style={{ fontSize: 28, marginBottom: 8 }}>⚔️</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>No objections logged yet</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>When the lead raises a concern, log it here and get AI-crafted responses in seconds.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {logs.map(log => {
            const sc = STATUS_COLORS[log.status] || STATUS_COLORS.Open;
            const isExpanded = expanded === log._id;
            const hasResponse = log.ai_response?.verbal || log.ai_response?.whatsapp || log.ai_response?.email;
            return (
              <Card key={log._id} style={{ padding: '14px 16px', border: `1.5px solid ${sc.border}` }}>
                {/* Card header row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                      {log.objection_category && (
                        <span style={{ fontSize: 11, fontWeight: 700, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: 10 }}>
                          {CATEGORY_ICONS[log.objection_category] || '💬'} {log.objection_category}
                        </span>
                      )}
                      <span style={{ fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, padding: '2px 8px', borderRadius: 10 }}>
                        {log.status}
                      </span>
                      {log.raised_by_contact && (
                        <span style={{ fontSize: 11, background: '#f3e8ff', color: '#7c3aed', border: '1px solid #ddd6fe', padding: '2px 8px', borderRadius: 10 }}>
                          👤 {log.raised_by_contact}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: '#94a3b8', padding: '2px 0' }}>
                        {new Date(log.raised_at).toLocaleDateString('en-IN')}
                      </span>
                    </div>
                    <p style={{ fontSize: 13, color: '#1e293b', fontStyle: 'italic', margin: 0 }}>
                      "{log.objection_text}"
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <Button size="sm" variant="primary" onClick={() => generateResponse(log)}
                      disabled={generating === log._id}>
                      {generating === log._id ? '✨ Generating…' : hasResponse ? '🔄 Regenerate' : '✨ Generate AI Response'}
                    </Button>
                    {hasResponse && (
                      <Button size="sm" variant="ghost" onClick={() => setExpanded(isExpanded ? null : log._id)}>
                        {isExpanded ? 'Hide Responses ▲' : 'Show Responses ▼'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* AI Response Cards */}
                {isExpanded && hasResponse && (
                  <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em', margin: 0 }}>AI-GENERATED RESPONSES</p>

                    {/* Verbal */}
                    {log.ai_response.verbal && (
                      <div style={{ background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 10, padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#0369a1' }}>📞 Verbal (Call Script)</span>
                          <CopyButton text={log.ai_response.verbal} />
                        </div>
                        <p style={{ fontSize: 13, color: '#0c4a6e', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{log.ai_response.verbal}</p>
                      </div>
                    )}

                    {/* WhatsApp */}
                    {log.ai_response.whatsapp && (
                      <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>💬 WhatsApp Message</span>
                          <CopyButton text={log.ai_response.whatsapp} />
                        </div>
                        <p style={{ fontSize: 13, color: '#14532d', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{log.ai_response.whatsapp}</p>
                      </div>
                    )}

                    {/* Email */}
                    {log.ai_response.email && (
                      <div style={{ background: '#fefce8', border: '1.5px solid #fde68a', borderRadius: 10, padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#a16207' }}>✉️ Email Response</span>
                          <CopyButton text={log.ai_response.email} />
                        </div>
                        <p style={{ fontSize: 13, color: '#713f12', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{log.ai_response.email}</p>
                      </div>
                    )}

                    {log.ai_response.generated_at && (
                      <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', margin: 0 }}>
                        Generated {new Date(log.ai_response.generated_at).toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                )}

                {/* Status updater */}
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                  <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>STATUS:</span>
                  {['Open', 'Addressed', 'Resolved', 'Unresolved'].map(s => {
                    const c = STATUS_COLORS[s];
                    return (
                      <button key={s} onClick={() => updateStatus(log, s)}
                        style={{ fontSize: 11, padding: '2px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontWeight: log.status === s ? 700 : 500,
                          background: log.status === s ? c.bg : '#f8fafc',
                          color: log.status === s ? c.color : '#94a3b8',
                          border: `1px solid ${log.status === s ? c.border : '#e2e8f0'}` }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Log Objection Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="Log Objection Raised">
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Select from Playbook
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>— or type a custom objection below</span>
            </label>
            <select value={form.objection_id} onChange={onSelectObjection} style={sel}>
              <option value="">— Custom / not in list —</option>
              {Object.entries(
                library.reduce((acc, o) => {
                  if (!acc[o.category]) acc[o.category] = [];
                  acc[o.category].push(o);
                  return acc;
                }, {})
              ).map(([cat, items]) => (
                <optgroup key={cat} label={`${CATEGORY_ICONS[cat] || ''} ${cat}`}>
                  {items.map(o => <option key={o._id} value={o._id}>{o.title}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Objection (as raised) *
            </label>
            <textarea value={form.objection_text}
              onChange={e => setForm(f => ({ ...f, objection_text: e.target.value }))}
              style={{ ...inp, minHeight: 72, resize: 'vertical' }}
              placeholder="Exact words or paraphrase of what the contact said…" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Category</label>
              <select value={form.objection_category} onChange={e => setForm(f => ({ ...f, objection_category: e.target.value }))} style={sel}>
                <option value="">Select category…</option>
                {['Fee', 'Timing', 'Authority', 'Competition', 'Relevance', 'Trust', 'Logistics', 'Other'].map(c =>
                  <option key={c} value={c}>{CATEGORY_ICONS[c]} {c}</option>
                )}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Raised By (Contact Name)</label>
              <input type="text" value={form.raised_by_contact}
                onChange={e => setForm(f => ({ ...f, raised_by_contact: e.target.value }))}
                style={inp} placeholder="e.g. Dean Sharma, Principal…" />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={logObjection} disabled={saving}>
              {saving ? 'Saving…' : 'Log & Generate AI Response'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
